<template>
    <li
        class="toolbar-tree__item"
        title="Right Click for more options"
        :class="itemClasses"
    >
        <template v-if="isFolder">
            <drop @drop="onDrop">
                <drag :hide-image-html="false" :transfer-data="item">
                    <button
                        v-show="!editMode"
                        class="toolbar-tree__button"
                        type="button"
                        @click.stop="toggle"
                        @dragenter="onDragEnter"
                        @dragleave="onDragLeave"
                        @contextmenu.prevent="openContextMenu"
                    >
                        <font-awesome-icon class="toolbar-tree__button-icon" :icon="['fal', folderIcon]" />
                        <span class="toolbar-tree__button-text">{{ item.displayName }}</span>
                        <font-awesome-icon class="toolbar-tree__button-arrow" :icon="['fal', accordionIcon]" />
                    </button>
                </drag>

                <input
                    v-show="editMode"
                    ref="input"
                    class="toolbar-tree-folder toolbar-tree__edit"
                    :value="itemName"
                    @keyup="onKeyup"
                >

                <collapse
                    :id="id"
                    v-model="open"
                    :accordion="accordionGroup"
                    role="tabpanel"
                >
                    <ul v-if="item.children && item.children.length" class="toolbar-tree__list">
                        <toolbar-insert-item
                            v-for="childItem in item.children"
                            :key="childItem.displayName"
                            :subcategory="hasSubcategory ? item.displayName : ''"
                            v-bind="passThroughProps(childItem)"
                        />
                    </ul>

                    <div v-else class="toolbar-tree__empty">
                        This folder is empty!
                    </div>
                </collapse>
            </drop>
        </template>

        <template v-else-if="toolbar === 'editor'">
            <drag class="toolbar-tree__component toolbar-tree__component--drag" :transfer-data="item" :hide-image-html="false">
                <span class="toolbar-tree__text">
                    {{ item.displayName }}
                </span>
            </drag>
        </template>

        <template v-else>
            <div class="toolbar-tree__component" :class="componentClasses">
                <drag v-if="isEditable" :transfer-data="item">
                    <font-awesome-icon class="toolbar-tree__grip" :icon="['fal', 'grip-lines']" />

                    <template slot="image">
                        <span class="toolbar-tree__text">
                            {{ itemName }}
                        </span>
                    </template>
                </drag>

                <span
                    v-show="!editMode"
                    class="toolbar-tree__text"
                    @click="onClick"
                    @contextmenu.prevent="openContextMenu"
                >
                    {{ itemName }}
                </span>

                <input
                    v-show="editMode"
                    ref="input"
                    class="toolbar-tree__edit"
                    :value="itemName"
                    @keyup="onKeyup"
                >
            </div>
        </template>
    </li>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';

import * as service from '@/sitecore/sitecore-data-service';
import { COMPONENT_TREE_ITEM_NAMES } from '@/store/types/getter-types';
import { LOAD_COMPONENT_TREE } from '@/store/types/action-types';
import {
    CLEAR_DELETED_MENU_DATA,
    SET_COPIED_MENU_DATA,
    SET_DELETED_MENU_DATA,
    SET_SELECTED_COMPONENT,
    SHOW_ALERT,
    SHOW_PROMPT,
    SHOW_SNACKBAR,
    SHOW_MODAL,
    CLEAR_VALIDATION_ERRORS,
} from '@/store/types/mutation-types';
import listenOnRootMixin from '@/mixins/listen-on-root';
import mapFilters from '@/utils/map-filters';
import { COMPONENT_TREE_SECTIONS } from '@/data/constants/component-tree-sections';
import { STATE_CLASSES } from '@/data/constants/classes';

export default {
    name: 'toolbar-insert-item',
    mixins: [clickaway, listenOnRootMixin],

    props: {
        item: {
            type: Object,
            required: true,
        },

        section: {
            type: String,
            default: '',
        },

        category: {
            type: String,
            default: '',
        },

        subcategory: {
            type: String,
            default: '',
        },

        toolbar: {
            type: String,
            default: '',
        },

        // represents the level inside the outer accordion ("standard components", "global content", etc)
        // ... which is why the level starts at 2
        level: {
            type: Number,
            default: 2,
        },
    },

    data() {
        return {
            dragToggleTimeout: 0,
            editMode: false,
            isHovered: false,
            itemName: this.item.displayName,
            loading: {
                copy: false,
                edit: false,
                delete: false,
            },
            open: false,
            validationMessage: '',
        };
    },

    computed: {
        ...mapGetters({
            componentTreeItemNames: COMPONENT_TREE_ITEM_NAMES,
        }),

        ...mapState({
            componentTree: state => state.page.componentTree,
            deletedMenuData: state => state.page.deletedMenuData,
            selectedComponent: state => state.page.selectedComponent,
        }),

        accordionGroup() {
            return `content-item-${ this.level }`;
        },

        accordionIcon() {
            return this.open ? 'chevron-up' : 'chevron-down';
        },

        folderIcon() {
            return this.open ? 'folder-open' : 'folder';
        },

        id() {
            return this.paramCase(this.item.displayName);
        },

        itemClasses() {
            return {
                [`toolbar-tree__item--level-${ this.level }`]: true,
                'toolbar-tree__item--is-item': !this.isFolder,
                [STATE_CLASSES.HOVERED]: this.isHovered,
            };
        },

        componentClasses() {
            return {
                'toolbar-tree__component--editable': this.isEditable,
            };
        },

        copyIcon() {
            return this.loading.copy ? 'spinner' : 'copy';
        },

        copyIconSet() {
            return this.loading.copy ? 'fas' : 'far';
        },

        editIcon() {
            switch (true) {
                case this.loading.edit:
                    return 'spinner';

                case this.editMode:
                    return 'save';

                default:
                    return 'pencil-alt';
            }
        },

        editIconSet() {
            return this.loading.edit ? 'fas' : 'far';
        },

        deleteIcon() {
            switch (true) {
                case this.loading.delete:
                    return 'spinner';

                default:
                    return 'trash-alt';
            }
        },

        deleteIconSet() {
            return this.loading.delete ? 'fas' : 'far';
        },

        isEditable() {
            const editableSections = [COMPONENT_TREE_SECTIONS.CLIENT, COMPONENT_TREE_SECTIONS.SHARED];

            return editableSections.includes(this.section);
        },

        isFolder() {
            if (this.isEditable) {
                return !this.item.props || !this.item.props.componentItemID;
            }

            return this.item.children && this.item.children.length;
        },

        nameExists() {
            return this.componentTreeItemNames.includes(this.itemName);
        },

        hasSubcategory() {
            return this.isFolder && this.level > 2;
        },
    },

    watch: {
        componentTree() {
            this.loading.edit = false;
            this.loading.delete = false;
        },
    },

    created() {
        this.setRootEventListeners();
    },

    methods: {
        ...mapFilters(['paramCase', 'titleCase']),

        ...mapActions({
            loadComponentTree: LOAD_COMPONENT_TREE,
        }),

        ...mapMutations({
            clearDeletedMenuData: CLEAR_DELETED_MENU_DATA,
            setCopiedMenuData: SET_COPIED_MENU_DATA,
            setDeletedMenuData: SET_DELETED_MENU_DATA,
            showAlert: SHOW_ALERT,
            showModal: SHOW_MODAL,
            setSelectedComponent: SET_SELECTED_COMPONENT,
            showPrompt: SHOW_PROMPT,
            showSnackbar: SHOW_SNACKBAR,
            clearValidationErrors: CLEAR_VALIDATION_ERRORS,
        }),

        setRootEventListeners() {
            if (this.isFolder) {
                this.listenOnRoot('flex::toolbar::show', this.show);
            }

            this.listenOnRoot('flex::component::copy::end', () => {
                this.loading.copy = false;
            });

            this.listenOnRoot('flex::component::delete::end', () => {
                this.loading.delete = false;
            });
        },

        passThroughProps(childItem) {
            return {
                ...this.$props,
                item: childItem,
                level: this.$props.level + 1,
            };
        },

        show({ section, category, id }) {
            const sectionMatch = this.titleCase(section) === this.section;
            const categoryMatch = !category || this.titleCase(category) === this.category;
            const idMatch = id === this.id;

            if (sectionMatch && categoryMatch && idMatch) {
                this.toggle(true);
            }
        },

        toggle(forceToggle) {
            if (this.isFolder) {
                const isOpen = typeof forceToggle === 'boolean' ? forceToggle : !this.open;
                const accordionEvent = isOpen ? 'flex::accordion::expanded' : 'flex::accordion::collapsed';

                this.open = isOpen;
                this.$root.$emit(accordionEvent);
            }
        },

        addChild() {
            this.item.children.push({
                name: this.item.name,
                displayName: 'New Item',
            });
        },

        onClick() {
            if (this.editMode) return;
            this.clearValidationErrors();
            this.$root.$emit('flex::component::select', this.item);
        },

        onAddFolder(data) {
            this.showModal({
                componentTag: 'add-folder-modal',
                data: {
                    id: data.id,
                },
            });
        },

        openContextMenu(event) {
            const element = this.item;

            this.$root.$emit('flex::context::open', {
                event,
                id: 'component-list-context-menu',
                data: {
                    id: this.item.itemId,
                    element,
                    isFolder: this.isFolder,
                    onAddCallback: this.onAddFolder,
                    onEditCallback: this.onEdit,
                    onMoveCallback: this.onMove,
                    onCopyCallback: this.onCopy,
                    onDeleteCallback: this.onDelete,
                },
            });
        },

        async onEdit() {
            if (this.editMode !== true) {
                this.editMode = true;
                await this.$nextTick();
                this.$refs.input.focus();
            } else {
                this.saveName();
            }
        },

        onKeyup(e) {
            if (e.key === 'Escape') {
                this.editMode = false;
                return;
            }

            this.itemName = e.target.value;

            if (e.key === 'Enter') {
                this.saveName();
            }
        },

        onDragEnter() {
            window.clearTimeout(this.dragToggleTimeout);

            this.dragToggleTimeout = window.setTimeout(() => {
                this.toggle(true);
            }, 250);

            this.isHovered = true;
        },

        onDragLeave() {
            window.clearTimeout(this.dragToggleTimeout);
            this.isHovered = false;
        },

        onDrop(data, event) {
            event.stopPropagation();

            if (
                this.toolbar === 'editor'
                || (this.section !== COMPONENT_TREE_SECTIONS.CLIENT && this.section !== COMPONENT_TREE_SECTIONS.SHARED)
                || data.itemId === this.item.itemId
            ) return;

            const itemName = data.displayName;
            const folderName = this.item.displayName;

            this.showPrompt({
                title: 'Confirm Item Placement',
                description: `This will move the "${ itemName }" item into the "${ folderName }" folder. Do you want to continue?`,
                onContinue: () => this.moveItem(data),
            });
        },

        async moveItem(data) {
            const response = await service.moveComponentMenu(data.itemId, this.item.itemId);

            if (response.status === 'error') {
                this.showAlert({ description: `An error was encountered while attempting to move the folder: ${ response.error }` });
                return;
            }

            // reload tree to get new data
            await this.loadComponentTree();

            this.showSnackbar({
                title: 'Success!',
                text: 'The folder has been successfully moved.',
            });
        },

        async saveName() {
            if (!this.itemName || this.itemName === this.item.displayName) {
                this.resetName();
                return;
            }

            if (this.nameExists) {
                this.showSnackbar({
                    title: 'Item edit failed.',
                    text: 'That name is already being used for another component.',
                });

                this.itemName = this.item.displayName;
                return;
            }

            if (this.editMode) {
                const itemNew = {
                    ...this.item,
                    displayName: this.itemName,
                };

                this.loading.edit = true;
                this.editMode = false;

                const response = await service.saveComponentName(itemNew.itemId, itemNew.displayName);

                if (response.status === 'error') {
                    this.showAlert({ description: `Error updating item: ${ response.error }` });
                }

                this.loadComponentTree();
            }
        },

        resetName() {
            this.editMode = false;
            this.itemName = this.item.displayName;
        },


        onCopy() {
            if (this.loading.copy) return;

            this.loading.copy = true;

            this.setCopiedMenuData({
                sectionName: this.section,
                categoryName: this.category,
                subcategoryName: this.subcategory,
                menuItem: this.item,
            });

            this.showModal('component-modal-save');
        },

        onDelete() {
            this.loading.delete = true;
            this.setDeletedMenuData(this.item);

            this.showPrompt({
                title: `Delete "${ this.item.displayName }"?`,
                description: 'This is a permanent action and cannot be undone. Do you want to continue?',
                onContinue: this.onDeleteConfirm,
                onCancel: this.onDeleteComplete,
            });
        },

        async onDeleteConfirm() {
            const itemId = this.deletedMenuData.itemId;
            const componentItemID = this.deletedMenuData.props?.componentItemID;
            const response = await service.deleteComponent(itemId, componentItemID);

            if (response.status === 'error') {
                this.onDeleteError(response.error);
                return;
            }

            if (this.selectedComponent && componentItemID === this.selectedComponent.id) {
                this.setSelectedComponent(null);
                this.$router.push({ path: '/components' });
            }

            await this.loadComponentTree();

            this.onDeleteComplete();
        },

        onDeleteError(msg) {
            this.showAlert({ description: `Error deleting item: ${ msg }` });
            this.onDeleteComplete();
        },

        onDeleteComplete() {
            this.clearDeletedMenuData();
            this.$root.$emit('flex::component::delete::end');
        },
    },
};
</script>
